/** React libraries */
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Form, Row, Col, Button, Toast } from "react-bootstrap";
import { toast } from "react-toastify";

/**  Custom components stores services types constant */
import CustomMultiSelect, { Option } from '../../../../components/Custom-Multiselect/CustomMultiSelect';
import ToggleSwitch from "./../../../../components/Toggle-Switch/ToggleSwitch";
import { DocumentUploadConstants, documentUploadService } from "./../../documentUploadService"
import uploadIcon from "../../../../assets/images/upload-icon.svg";
import { ProductTypeString, RequestMetadaContext } from "../../logoRequest.Type";
import { useLoader } from "../../../../context/LoaderContext";
import { STATUS } from "../../../../utils/statusHandler";
import { CERTIFICATE_STATUS } from "../../../Certificates/certificates.types";

/** styles */
import "./style.css";

interface Props {
  onSubmitLabelAgreement: () => void;
}

const OnProductRequestDetail: React.FC<Props> = ({ onSubmitLabelAgreement }) => {
  const [labelingAgreement, setLabelingAgreement] = useState<string | null>(null);
  const { setValue, watch, register, getValues } = useFormContext();
  const [isOn, setIsOn] = useState(false);
  const requestMetadaContext = useContext(RequestMetadaContext);
  const { setLoading } = useLoader();
  const status = getValues("status");
  const labelingAgreementVisibility = (status === STATUS.DRAFT || status === STATUS.CHANGE_REQUESTED);
  const isReadOnly = watch("isReadOnly") ? true : false;
  const isAgreementShareFilePath =  watch("agreementShareFilePath");
  const uploadButtonRef = useRef<HTMLButtonElement>(null);
  const certificate = getValues("certificate");

  useEffect(() => {
    if (getValues("requestDetailsData.fscLicenceCode") === "Yes") {
      setIsOn(true);
      const newFileName = `${requestMetadaContext.logoRequestName}_Labeling_Agreement`;
      setLabelingAgreement(newFileName); 
    }
  }, []);

  /**
   * Is file name safe
   * @param name 
   * @returns 
   */
  const isSafeFileName = (name: string) =>
  !name.includes('..') && !name.includes('/') && !name.includes('\\');

  /**
   * Is file valid
   * @param file 
   * @returns 
   */
  const isValidFile = (file: File): string | null => {
    const ext = file.name.split('.').pop()?.toLowerCase();

    if (!ext || !DocumentUploadConstants.AGREEMENT_ALLOWED_EXTENSIONS.includes(ext)) {
      return DocumentUploadConstants.FILE_TYPE_NOT_SUPPORT_MSG;
    }

    if (!DocumentUploadConstants.AGREEMENT_ALLOWED_MIME_TYPES.includes(file.type)) {
      return DocumentUploadConstants.FILE_TYPE_NOT_SUPPORT_MSG;
    }

    if (!isSafeFileName(file.name)) {
      return DocumentUploadConstants.INVALID_FILE_NAME;
    }

    return null;
  };

  /**
   * Handle toogle
   */
  const handleToggle = () => {
    const newValue = !isOn;
    let labelValue = "No";
    if (newValue) {
      labelValue = "Yes";
    }
    setValue("requestDetailsData.fscLicenceCode", labelValue);
    setIsOn(newValue);
    if (labelingAgreement && labelingAgreement != "" && !newValue) {
      onSubmitLabelAgreement();
      setLabelingAgreement("");
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const newFiles = Array.from(e.target.files || []);
      const file = e.target.files ? e.target.files[0] : null;
      if (file) {
        const error = isValidFile(file);
        if (error) {
          toast.error(`${file.name}: ${error}`);
          return;
        }
        if (newFiles.every(file => file.size > DocumentUploadConstants.AGREEMENT_FILE_MAX_SIZE_LIMIT * 1024 * 1024 + 5)) {
          toast.error(DocumentUploadConstants.FILE_SIZE_LIMIT_MSG);
          return;
        } else {
          let folderPath = getValues("customFolderPath");
          // Validate is path present or not if not the fetch logo request
          if (!folderPath) {
            toast.error(DocumentUploadConstants.UPLOAD_DOCUMENTS_FAIL_MSG);
            return;
          }
          
          folderPath = folderPath + "/LabelingAgreement";

          const newFileName = `${requestMetadaContext.logoRequestName}_Labeling_Agreement`;
          const ext = file.name.split('.').pop();
          const updatedName = `${newFileName}.${ext}`;

          // Create a new file with the same content but different name
          const renamedFile = new File([file], updatedName, {
            type: file.type,
            lastModified: file.lastModified,
          });

          setLoading(true);
          const response = await documentUploadService.uploadFile(folderPath, renamedFile);
          let labelingAgreementPath = getValues("agreementShareFilePath");
          setValue("requestDetailsData.labelingAgreementPath", labelingAgreementPath);

          setLabelingAgreement(updatedName);
          setLoading(false);

          if (response) {
            toast.success(DocumentUploadConstants.AGREEMENT_UPLOAD_SUCC_MSG);
            onSubmitLabelAgreement();
          } else {
            toast.error(DocumentUploadConstants.AGREEMENT_UPLOAD_FAIL_MSG);
          }
        }
      }
    } catch (exe: any) {
      setLoading(false);
      let errorMessage = exe.message || DocumentUploadConstants.AGREEMENT_UPLOAD_FAIL_MSG;
      toast.error(errorMessage);
    }
  };

  // all site names
  const allSiteMetaData = watch("requestDetailsData.allSiteMetaData");


  // selected site names
  const selectedSiteNames = watch("requestDetailsData.siteName");

  // Is labeling agrrement disabled
  const isLabelingAgreementDisabled = certificate?.status !== CERTIFICATE_STATUS.CERTIFIED;

  /**
   * Selected multiselect items
   * @param selectedItems 
   */
  const onSiteSelectionChange = (selectedItems: string[]) => {
    setValue("requestDetailsData.siteName", selectedItems, { shouldDirty: true });
  }

  return (
    <div>
      <Row>
        <Col xxl={6} xs={6}>
          <Row className="mb-3">
            <Col xs={5}>
              <Form.Label className="secondary-font">Company Name</Form.Label>
            </Col>
            <Col xs={7}>
              <Form.Control
                disabled={true}
                type="text"
                placeholder="Company Name"
                {...register("requestDetailsData.companyName")}
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col xs={5}>
              <Form.Label className="secondary-font">FSC Trademark License Code</Form.Label>
            </Col>
            <Col xs={7}>
              <Form.Control
                disabled={true}
                type="text"
                placeholder="License Code"
                {...register("requestDetailsData.fscTrademarkCode")}
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col xs={5}>
              <Form.Label className="secondary-font">SCS Certification Number</Form.Label>
            </Col>
            <Col xs={7}>
              <Form.Control
                disabled={true}
                type="text"
                placeholder="Certification Number"
                {...register("requestDetailsData.scsCertificationNumber")}
              />
            </Col>
          </Row>
        </Col>

        <Col xxl={6} xs={6}>
          <Row className="mb-4">
            <Col xs={4}>
              <Form.Label className="secondary-font">Site Name</Form.Label>
            </Col>
            <Col xs={8}>
              {!isReadOnly &&
                <CustomMultiSelect
                  options={allSiteMetaData}
                  selected={selectedSiteNames}
                  onChange={onSiteSelectionChange}
                  placeholder="Select site"
                  disabled={isReadOnly}
                />
              }

              {isReadOnly &&
                <div className="d-flex justify-content-between site-Name">
                  {selectedSiteNames?.join(", ")}
                </div>
              }
            </Col>
          </Row>
          <Row style={{ marginBottom: "16px" }}>
            <Col xs={4}>
              <Form.Label className="secondary-font">Job Name</Form.Label>
            </Col>
            <Col xs={8}>
              <Form.Control
                type="text"
                placeholder="Job Name"
                {...register("requestDetailsData.jobName")}
                disabled={isReadOnly}
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col xs={4}>
              <Form.Label className="secondary-font">Job Number</Form.Label>
            </Col>
            <Col xs={8}>
              <Form.Control
                type="text"
                placeholder="Job Number"
                {...register("requestDetailsData.jobNumber")}
                disabled={isReadOnly}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      {requestMetadaContext.requestType == ProductTypeString.OnProduct && <>
        <Row className="pt-3">
          {(!isAgreementShareFilePath || isAgreementShareFilePath === "") ? (
            <Col xxl={6} xs={6}>
              <div className="d-flex justify-content-center align-items-center" style={{ height: '100px' }}>
                Loading...
              </div>
            </Col>
          ) : labelingAgreementVisibility ? (
            <>
              <Col xxl={6} xs={6}>
                <Row>
                  <Col xs={10}>
                    <Form.Label className="secondary-font">
                      Have you been given permission to use another account's FSC license code?
                    </Form.Label>
                  </Col>
                  <Col xs={2}>
                    <ToggleSwitch isOn={isOn} handleToggle={handleToggle} onLabel="Yes" offLabel="No" disabled={isLabelingAgreementDisabled} />
                  </Col>
                </Row>
                {isOn && (
                  <Row className="mb-3">
                    <p className="warning-text fs-14">
                      Submitting another account's FSC License Code without a legitimate labeling agreement will result in automatic rejection of request
                    </p>
                    <Col xxl={12} xs={12}>
                      <Form.Control
                        type="file"
                        id="labeling-agreement"
                        onChange={handleFileChange}
                        style={{ display: "none" }}
                      />
                      <Button
                        ref={uploadButtonRef}
                        variant="outline-secondary"
                        className="upload-button"
                        onClick={() =>{
                          uploadButtonRef?.current?.blur()
                          document.getElementById("labeling-agreement")?.click()
                        }}
                      >
                        <img src={uploadIcon} alt="Upload" className="upload-icon" />
                        Submit the labeling agreement
                      </Button>
                      {labelingAgreement && (
                        <div className="file-info mt-2">
                          <p>File selected: {labelingAgreement}</p>
                        </div>
                      )}
                    </Col>
                  </Row>
                )}
              </Col>
              <Col xxl={6} xs={6}></Col>
            </>
          ) : (
            <>
              <Col xxl={6} xs={6}>
                {isOn ? (
                  <div className="label-agreement-view">The labeling agreement has been attached.</div>
                ) : (
                  <div className="label-agreement-view">The labeling agreement has not been attached.</div>
                )}
              </Col>
              <Col xxl={6} xs={6}></Col>
            </>
          )}
        </Row>
      </>
      }
    </div>
  );
};

export default OnProductRequestDetail;